import React, { useState, useEffect, useCallback } from 'react';
import { getUserCurrencyBalance } from '../../DatabaseAPI';
import { Button } from '../../components/ui';
import { STORE_CATEGORY_MAP, STORE_CATEGORIES } from './storeCategories';
import './StoreCategoryPage.css';

/**
 * StoreCategoryPage — generic store category screen.
 *
 * Replaces the per-category SkinsPage (and would-be BackgroundsPage / FoilsPage /
 * TauntsPage). ONE component renders any category, chosen by the `category` prop.
 * Categories with items show a product grid; empty ones show a "coming soon"
 * placeholder. Category data lives in storeCategories.js.
 *
 * Props:
 *   @param {Object}   user     - authenticated user
 *   @param {string}   category - category key ('skins' | 'backgrounds' | 'foils' | 'taunts')
 *   @param {Function} setPage  - navigation callback. Destination used:
 *                                'storePage' (back to the store hub). Both the top
 *                                nav BACK button and the empty-state "Back to Store"
 *                                button send it; this screen never navigates to
 *                                'myTeams', which is why the top control reads BACK
 *                                rather than LEAGUES.
 */
function StoreCategoryPage({ user, category = 'skins', setPage }) {
    const [mythrilBalance, setMythrilBalance] = useState(10000); // Hardcoded for prototype

    const config = STORE_CATEGORY_MAP[category] || STORE_CATEGORIES[0];
    const items = config.items || [];

    const fetchCurrencyBalance = useCallback(async () => {
        try {
            const balance = await getUserCurrencyBalance();
            setMythrilBalance(balance.current_balance || 10000);
        } catch (error) {
            console.error('StoreCategoryPage: Error fetching currency balance:', error);
            setMythrilBalance(10000); // Fallback to prototype value
        }
    }, []);

    useEffect(() => {
        // Uncomment when backend is ready
        // fetchCurrencyBalance();
        // Also: fetch per-category items here keyed by `category`.
    }, [fetchCurrencyBalance, category]);

    const handleBackClick = () => {
        console.log('StoreCategoryPage: Navigating back to Store');
        if (setPage) {
            setPage('storePage');
        }
    };

    const handleBuyClick = (item) => {
        console.log(`StoreCategoryPage: Buy clicked for ${category} item ${item.id} at price ${item.price}`);
        // Placeholder for purchase logic
    };

    // Singular preview label, e.g. "Skins" -> "SKIN"
    const previewLabel = config.label.replace(/s$/, '').toUpperCase();

    return (
        <div className="store-category-page">
            {/* Top Navigation Bar */}
            <div className="store-category-nav-bar">
                {/* Labelled BACK, not LEAGUES: handleBackClick returns to the Store
                    hub ('storePage'), never to the Leagues hub — see the prop docs. */}
                <Button
                    variant="secondary"
                    size="md"
                    className="store-category-nav-button store-category-nav-button--back"
                    iconLeft={<span className="store-category-nav-arrow" aria-hidden="true">◀</span>}
                    onClick={handleBackClick}
                >
                    BACK
                </Button>

                <div className="store-category-currency-display">
                    <img
                        src="/art/diamond_400_white_bg.svg"
                        alt="Mythril"
                        className="store-category-currency-icon"
                    />
                    <span className="store-category-currency-label">
                        {mythrilBalance} MYTHRIL
                    </span>
                </div>
            </div>

            {/* Category title */}
            <h1 className="store-category-title">{config.label}</h1>

            {/* Body: product grid OR "coming soon" placeholder */}
            {items.length > 0 ? (
                <div className="store-category-grid" aria-label={`${config.label} for sale`}>
                    {items.map((item) => (
                        <div key={item.id} className="store-category-card">
                            <div className="store-category-card__preview">
                                <span>{previewLabel}</span>
                            </div>
                            <div className="store-category-card__name">{item.name}</div>
                            <button
                                type="button"
                                className="store-category-card__buy"
                                onClick={() => handleBuyClick(item)}
                            >
                                <span aria-hidden="true">💎</span>
                                <span>{item.price}</span>
                            </button>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="store-category-body">
                    <div className="store-category-placeholder">
                        <span className="store-category-placeholder__tag">
                            {config.label} — coming soon
                        </span>
                        <span>This shop section is not built yet.</span>
                    </div>
                    <button
                        type="button"
                        className="store-category-back"
                        onClick={handleBackClick}
                    >
                        <span aria-hidden="true">◀</span>
                        <span>Back to Store</span>
                    </button>
                </div>
            )}
        </div>
    );
}

export default StoreCategoryPage;
